import React, { createContext, forwardRef, useContext, } from 'react';
import { Text, TouchableWithoutFeedback, View, } from 'react-native';
import Responsive from "../../responsive";
import mergeStyles from "../../util/mergeStyles";
import Box from "../../layout/Box/Box";
import findChildren from "../../../util/findChildren";
/** Context */
const GridListContext = createContext({ baseS: { grid: [] } });
GridListContext.displayName = 'GridListContext';
const useGridList = () => useContext(GridListContext);
/** Style */
const S = {
    UserDefinedCols: (cols) => typeof cols === 'number'
        ? { gridTemplateColumns: `repeat(${cols}, minmax(0px, 1fr))` }
        : typeof cols === 'string'
            ? { gridTemplateColumns: cols }
            : { gridTemplateColumns: cols?.join(' ') },
    FixedCols: (cols) => ({
        gridTemplateColumns: `repeat(${cols}, minmax(0px, 1fr))`,
    }),
    ColSpan: (span) => ({
        gridColumn: `span ${span}`,
    }),
    Col: {
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
    },
};
function wrapPressable(renderedComponent, onPress) {
    return onPress
        ? (<TouchableWithoutFeedback onPress={onPress}>
        {renderedComponent}
      </TouchableWithoutFeedback>)
        : renderedComponent;
}
/** Components */
const GridList = (props) => {
    const { children, ...rest } = props;
    return (<Box {...rest} style={{ flex: 1 }}>
      <GridListProvider {...props}>
        {children}
      </GridListProvider>
    </Box>);
};
const GridListProvider = (props) => {
    const { children, cols, gap } = props;
    const [headerChildren] = findChildren(GridList.Header, children);
    // Ignore if multiple <Header> are included
    const headerColsLength = headerChildren?.[0]?.props?.children?.length;
    const values = {
        baseS: {
            grid: mergeStyles(props, [{ display: 'grid' }, Responsive.device.isWeb], [{ gap }, Responsive.device.isWeb], [S.FixedCols(headerColsLength), headerColsLength && Responsive.device.isWeb], [S.UserDefinedCols(cols), !!cols && Responsive.device.isWeb]),
        },
    };
    return (<GridListContext.Provider value={values}>
      {children}
    </GridListContext.Provider>);
};
const Header = forwardRef((props, ref) => {
    const { children, ...rest } = props;
    const { baseS } = useGridList();
    return (<Box ref={ref} {...rest} style={mergeStyles(props, [baseS.grid, Responsive.device.isWeb])}>
      {children}
    </Box>);
});
// [TODO]: Figure out explicit any
const Row = (props) => {
    const { as, children, onPress, interactive, disabled, ...rest } = props;
    const { baseS } = useGridList();
    const [ColChildren, beforeChildren, afterChildren] = findChildren(Col, children);
    const ContainerComponent = as && !interactive ? as : View; // [TODO] - Better pressable usage
    const renderedRow = (
    // @ts-ignore
    <ContainerComponent pointerEvents={disabled ? 'none' : undefined} style={mergeStyles(props)} {...rest}>
      {beforeChildren}
      <Box style={mergeStyles({}, [baseS.grid, Responsive.device.isWeb])}>{ColChildren}</Box>
      {afterChildren}
    </ContainerComponent>);
    return wrapPressable(renderedRow, onPress);
};
const Col = (props) => {
    const { children, onPress, span, align, noWrap = false } = props;
    const colSpan = span ? S.ColSpan(span) : '';
    const isString = typeof children === 'string';
    const renderedCol = (<Box style={mergeStyles(props, S.Col, { justifyContent: align === 'right' ? 'flex-end' : 'flex-start' }, [colSpan, span])}>
      {isString
            ? <Text numberOfLines={noWrap ? undefined : 1}>{children}</Text>
            : children}
    </Box>);
    return wrapPressable(renderedCol, onPress);
};
Header.displayName = 'Header';
Col.displayName = 'Col';
GridList.displayName = 'GridList';
Row.displayName = 'Row';
GridList.Header = Header;
GridList.Col = Col;
GridList.Row = Row;
export { GridListContext, useGridList, };
export default GridList;
//# sourceMappingURL=GridList.js.map